import React from "react";

const API = "https://api.briolabs.io";

const theme = {
  bg: "#0B0F1A",
  card: "#121827",
  card2: "#171F32",
  border: "#2A3550",
  text: "#F8FAFC",
  muted: "#94A3B8",
  dim: "#64748B",
  accent: "#8B5CF6",
  success: "#22C55E",
  warning: "#F59E0B",
  cyan: "#38BDF8",
};

function Pill({ children, color = theme.accent }) {
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color,
      background: `${color}18`,
      border: `1px solid ${color}44`,
    }}>
      {children}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div style={{
      padding: 14,
      borderRadius: 14,
      background: theme.card2,
      border: `1px solid ${theme.border}`,
    }}>
      <div style={{ fontSize: 11, color: theme.muted, marginBottom: 6, letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, color: theme.text, fontWeight: 800 }}>
        {value}
      </div>
    </div>
  );
}

function TreeNode({ icon, label, value, children, accent = theme.accent }) {
  return (
    <div style={{
      marginBottom: 14,
      padding: 18,
      borderRadius: 18,
      background: `linear-gradient(135deg, ${theme.card}, ${theme.card2})`,
      border: `1px solid ${theme.border}`,
      boxShadow: "0 14px 40px rgba(0,0,0,0.22)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 42,
          height: 42,
          borderRadius: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `${accent}18`,
          border: `1px solid ${accent}44`,
          fontSize: 22,
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 12, color: theme.muted, fontWeight: 700, letterSpacing: 0.8 }}>
            {label}
          </div>
          <div style={{ fontSize: 22, color: theme.text, fontWeight: 900 }}>
            {value}
          </div>
        </div>
      </div>

      {children && (
        <div style={{
          marginTop: 16,
          marginLeft: 21,
          paddingLeft: 22,
          borderLeft: `2px solid ${accent}55`,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function PilotStructurePage({ account }) {
  const tenantName = account?.display_name || account?.name || "brioCAST Pilot Tenant";
  const accountId = account?.account_id || account?.id || "PUB-F2-PILOT";

  return (
    <div style={{ minHeight: "100%", background: theme.bg, color: theme.text, padding: 28 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        marginBottom: 24,
      }}>
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <Pill color={theme.success}>F2-EV-001 Ready</Pill>
            <Pill color={theme.cyan}>Partner Portal Runtime Evidence</Pill>
            <Pill color={theme.warning}>Capture Target</Pill>
          </div>
          <h1 style={{ margin: 0, fontSize: 32, lineHeight: 1.1, fontWeight: 950 }}>
            場域結構 / Pilot CMS Structure
          </h1>
          <p style={{ margin: "10px 0 0", color: theme.muted, fontSize: 14 }}>
            Evidence artifact for Tenant → Site / Venue → Zone → Screen hierarchy.
          </p>
        </div>

        <div style={{
          minWidth: 260,
          padding: 16,
          borderRadius: 18,
          background: theme.card,
          border: `1px solid ${theme.border}`,
        }}>
          <div style={{ fontSize: 11, color: theme.muted, marginBottom: 8 }}>Evidence ID</div>
          <div style={{ fontSize: 22, fontWeight: 950, color: theme.text }}>F2-EV-001</div>
          <div style={{ marginTop: 10, fontSize: 12, color: theme.dim }}>Backend: {API}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.35fr 0.65fr", gap: 18 }}>
        <div>
          <TreeNode icon="🏢" label="TENANT / PARTNER ACCOUNT" value={tenantName} accent={theme.accent}>
            <TreeNode icon="📍" label="SITE / VENUE" value="F2 Pilot Demo Venue" accent={theme.cyan}>
              <TreeNode icon="🧭" label="ZONE / SCREEN AREA" value="Main Counter Zone" accent={theme.warning}>
                <TreeNode icon="📺" label="SCREEN / DEVICE" value="F2 Pilot Main Display" accent={theme.success} />
              </TreeNode>
            </TreeNode>
          </TreeNode>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Tenant ID" value={accountId} />
          <Field label="Site / Venue ID" value="site_f2_pilot_demo_001" />
          <Field label="Zone ID" value="zone_main_counter_001" />
          <Field label="Screen ID" value="screen_f2_pilot_main_001" />
          <Field label="Owner" value="brioCAST" />
          <Field label="Artifact Path" value="docs/pilot/evidence/screenshots/F2-EV-001-site-zone-setup.png" />
        </div>
      </div>

      <div style={{
        marginTop: 20,
        padding: 18,
        borderRadius: 18,
        background: "#062315",
        border: "1px solid #22C55E55",
        color: "#DCFCE7",
        fontSize: 14,
        lineHeight: 1.7,
      }}>
        <strong>PASS requirement:</strong> this screen visibly proves the Partner/CMS hierarchy:
        Tenant → Site / Venue → Zone → Screen. Capture this page as the F2-EV-001 runtime evidence artifact.
      </div>
    </div>
  );
}
